import { useState } from "react";

import {
  Typography,
  Divider,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Avatar,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { CategoriesAvatar } from "@aclymatepackages/atoms";
import { hexToRgba } from "@aclymatepackages/converters";
import { subcategories } from "@aclymatepackages/subcategories";

import IdDisplayChip from "../../atoms/IdDisplayChip";
import TagAvatar from "../../atoms/icons/TagAvatar";
import EditableNameInput from "../../atoms/inputs/EditableNameInput";

import ManualTransactionsInput from "../../inputs/emissions/ManualTransactionsInput";
import SlideLayout from "../../layouts/SlideLayout";
import DocumentationUploader from "../../modules/file-uploaders/DocumentationUploader";
import TransactionNotes from "../../modules/TransactionNotes";

const EmissionsDetailsFooter = ({
  name,
  emissionsType,
  onManualTransactionSelect,
  inputSubcategories,
  otherInputs = []
}) => {
  const filteredSubcategories = subcategories.filter(
    ({ input, tags = [] }) => input && tags.includes(emissionsType)
  );

  const displaySubcategories = inputSubcategories || filteredSubcategories;

  return (
    <>
      {!!displaySubcategories.length && (
        <Grid container justifyContent="center" spacing={1} direction="column">
          <Grid item>
            <Typography variant="h6" align="center">
              {`Add more emissions for ${name}`}
            </Typography>
          </Grid>
          <Grid container item justifyContent="center" spacing={1}>
            {displaySubcategories.map(({ subcategory }, idx) => (
              <Grid item key={`subcategory-selection-card-${idx}`}>
                <IconButton
                  size="small"
                  onClick={() => onManualTransactionSelect(subcategory)}
                >
                  <CategoriesAvatar subcategory={subcategory} tooltip />
                </IconButton>
              </Grid>
            ))}
            {!!otherInputs.length &&
              otherInputs.map(({ icon, onClick, tooltip, color }, idx) => (
                <Grid item key={`other-input-button-${idx}`}>
                  <Tooltip title={tooltip}>
                    <IconButton onClick={onClick} size="small">
                      <Avatar style={color ? { backgroundColor: color } : {}}>
                        <FontAwesomeIcon icon={icon} />
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </Grid>
              ))}
          </Grid>
        </Grid>
      )}
    </>
  );
};

const SettingsObjectDetailsSlider = ({
  type,
  objectName: parentObjectName,
  settingsObject,
  editSettingsObject,
  onNameSave,
  setSelectedObject,
  views = [],
  noFooter,
  footerInputSubcategories,
  leftContent,
  editNameDisabled,
  otherFooterInputs = [],
  avatar,
  documentationContent,
  headerAction
}) => {
  const { name: originalName, id } = settingsObject;
  const [{ value: firstViewValue }] = views;

  const name = parentObjectName ?? originalName;

  const { palette } = useTheme();

  const [selectedView, setSelectedView] = useState(firstViewValue);
  const [notes, setNotes] = useState(settingsObject.notes || []);

  const [
    manualTransactionsInputSubcategory,
    setManualTransactionsInputSubcategory
  ] = useState(false);

  const handleNotesUpdate = (updatedNotes) => {
    setNotes(updatedNotes);
    if (editSettingsObject) {
      editSettingsObject("notes")(updatedNotes);
    }
  };

  const viewsWithDocumentation = [
    ...views,
    {
      label: "Documentation",
      value: "documentation",
      content: (
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <TransactionNotes
              collection={type}
              docId={id}
              notes={notes}
              editField={handleNotesUpdate}
            />
          </Grid>
          <Grid item>
            {documentationContent || (
              <DocumentationUploader type={type} docId={id} docName={name} />
            )}
          </Grid>
        </Grid>
      )
    }
  ];

  const { value, content, footer, contentPosition } =
    viewsWithDocumentation.find(({ value }) => value === selectedView) || {};

  const buildTagProps = () => {
    if (type === "employees") {
      return { employees: [settingsObject] };
    }
    if (type === "vendors") {
      return {
        vendorId: settingsObject.vendorId,
        vendorName: settingsObject.name
      };
    }

    const singleType = type.slice(0, -1);
    return { [singleType]: settingsObject };
  };

  const availableSubcategories = () => {
    if (!footerInputSubcategories) {
      return subcategories.filter(
        ({ input, tags = [] }) => input && tags.includes(type)
      );
    }

    const categories = footerInputSubcategories.map(
      ({ subcategory }) => subcategory
    );

    return subcategories.filter(
      ({ input, subcategory, tags = [] }) =>
        input &&
        (tags.includes(type) || type === "offices") &&
        categories.includes(subcategory)
    );
  };

  return (
    <>
      {manualTransactionsInputSubcategory ? (
        <ManualTransactionsInput
          setDialogOpen={setSelectedObject}
          startingTransactionProps={{
            subcategory: manualTransactionsInputSubcategory,
            ...buildTagProps()
          }}
          availableSubcategories={availableSubcategories()}
          emissionsAddName={originalName}
        />
      ) : (
        <SlideLayout
          isSlideOpen
          header={
            <Grid container spacing={2} wrap="nowrap" alignItems="center">
              <Grid item>{avatar || <TagAvatar tag={type} />}</Grid>
              <Grid item sm={6}>
                {editNameDisabled ? (
                  <Typography variant="h5">{name}</Typography>
                ) : (
                  <EditableNameInput
                    name={name}
                    editName={editSettingsObject && editSettingsObject("name")}
                    id={id}
                    collection={type}
                    onNameSave={onNameSave}
                  />
                )}
              </Grid>
              {id && (
                <Grid item sm={6}>
                  <IdDisplayChip id={id} type={type} />
                </Grid>
              )}
              {headerAction && (
                <Grid item sx={{ ml: "auto" }}>
                  {headerAction}
                </Grid>
              )}
            </Grid>
          }
          tagInputs={
            views.length > 1 && (
              <>
                <Tabs
                  value={value}
                  onChange={(_, value) => setSelectedView(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {[
                    ...views,
                    { label: "Documentation", value: "documentation" }
                  ].map(({ label, value }, idx) => (
                    <Tab
                      label={label}
                      value={value}
                      key={`object-details-view-tab-${idx}`}
                    />
                  ))}
                </Tabs>
                <Divider />
              </>
            )
          }
          content={content}
          contentPosition={contentPosition}
          footer={
            !noFooter &&
            (footer ||
              (selectedView === "emissions" && (
                <EmissionsDetailsFooter
                  inputSubcategories={footerInputSubcategories}
                  name={originalName}
                  emissionsType={type}
                  onManualTransactionSelect={(subcategory) =>
                    setManualTransactionsInputSubcategory(subcategory)
                  }
                  otherInputs={otherFooterInputs}
                />
              )))
          }
          footerColor={
            !noFooter &&
            selectedView === "emissions" &&
            (!footerInputSubcategories || !!footerInputSubcategories.length) &&
            hexToRgba(palette[type].main, 0.1)
          }
          setIsSlideOpen={setSelectedObject}
          leftContent={leftContent}
        />
      )}
    </>
  );
};
export default SettingsObjectDetailsSlider;
